import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

// Colored badge palette (light bg + darker text pills), matching the Call Logs
// label style, so distinct values are easy to tell apart.
export const BADGE_PALETTE = [
  { bg: 'bg-blue-50', text: 'text-blue-700' },
  { bg: 'bg-green-50', text: 'text-green-700' },
  { bg: 'bg-purple-50', text: 'text-purple-700' },
  { bg: 'bg-orange-50', text: 'text-orange-700' },
  { bg: 'bg-pink-50', text: 'text-pink-700' },
  { bg: 'bg-teal-50', text: 'text-teal-700' },
  { bg: 'bg-amber-50', text: 'text-amber-700' },
  { bg: 'bg-sky-50', text: 'text-sky-700' },
  { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  { bg: 'bg-rose-50', text: 'text-rose-700' },
]

/** Palette slot at `index`, wrapping around for sets larger than the palette. */
export function paletteAt(index: number) {
  const slot =
    ((index % BADGE_PALETTE.length) + BADGE_PALETTE.length) %
    BADGE_PALETTE.length
  return BADGE_PALETTE[slot]!
}

/** Stable color for a value, derived from its text. */
export function paletteForValue(value: string) {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  }
  return paletteAt(Math.abs(hash))
}

interface ColorBadgeProps {
  value: string
  /**
   * Fixed palette slot. Use when a known, bounded set needs guaranteed-distinct
   * colors; omit to derive a stable color by hashing `value`, which is right for
   * open-ended sets but can collide.
   */
  colorIndex?: number
  className?: string
}

export function ColorBadge({ value, colorIndex, className }: ColorBadgeProps) {
  const color =
    colorIndex === undefined ? paletteForValue(value) : paletteAt(colorIndex)

  return (
    <Badge
      className={cn(
        'rounded-md border-transparent',
        color.bg,
        color.text,
        className
      )}>
      {value}
    </Badge>
  )
}
